import { useState, useEffect, useCallback, useRef, type FC, Fragment } from 'react';
import { graphql, usePaginationFragment } from 'react-relay';
import { trackEcommerce, trackingConstants, eventNameConstants } from 'dibs-tracking';
import { FormattedMessage, useIntl } from 'dibs-react-intl';
import { getLang } from 'dibs-intl/exports/languages';

import { Spinner } from 'dibs-elements/exports/Spinner';
import { Button } from 'dibs-elements/exports/Button';
import { TabProvider } from 'dibs-elements/exports/TabProvider';
import { TabPanels } from 'dibs-elements/exports/TabPanels';
import { type TranslationState } from 'dibs-elements/exports/TranslationToggle';

import {
    SellerReviewsModalHeader,
    REVIEWS_TAB,
    PHOTOS_TAB,
} from './SellerReviewsModalHeader/SellerReviewsModalHeader';
import { Review } from './Review/Review';
import { SellerReviewsPhotosCarouselRenderer } from './SellerReviewsPhotosCarouselRenderer';
import { ORDER_BY_NEWEST, ORDER_BY_HIGHEST, ORDER_BY_LOWEST, type OrderBy } from '../constants';

import { type SellerReviewsMobileModalContentRefetchQuery } from './__generated__/SellerReviewsMobileModalContentRefetchQuery.graphql';
import {
    type SellerReviewsMobileModalContent_viewer$key,
    type SellerReviewsMobileModalContent_viewer$data,
} from './__generated__/SellerReviewsMobileModalContent_viewer.graphql';

import styles from './SellerReviewsMobileModalContent.scss';
import classnames from 'classnames';

type ReviewsType = SellerReviewsMobileModalContent_viewer$data['paginatedFeedback'];

type ReviewType = NonNullable<NonNullable<NonNullable<ReviewsType>['edges']>[0]>['node'];

function normalizeReviews(reviews?: ReviewsType): Array<ReviewType> {
    if (reviews && reviews.edges) {
        return reviews.edges.map(edge => edge && edge.node);
    } else {
        return [];
    }
}

const PAGE_SIZE = 10;

type Props = {
    viewer: SellerReviewsMobileModalContent_viewer$key | null | undefined;
    sellerId: string;
    isLoading?: boolean;
    contentOnly?: boolean;
};

const viewerFragment = graphql`
    fragment SellerReviewsMobileModalContent_viewer on Viewer
    @refetchable(queryName: "SellerReviewsMobileModalContentRefetchQuery")
    @argumentDefinitions(
        count: { type: "Int", defaultValue: 10 }
        cursor: { type: "String" }
        orderByNewest: { type: "Boolean", defaultValue: true }
        orderByHighest: { type: "Boolean", defaultValue: false }
        orderByLowest: { type: "Boolean", defaultValue: false }
    ) {
        feedbackFormPhotoUpload: featureFlag(feature: "feedbackFormPhotoUpload")
        paginatedFeedback: orderFeedbackMultiple(
            sellerId: $sellerId
            excludeInAppealProcess: true
            orderByNewest: $orderByNewest
            orderByHighest: $orderByHighest
            orderByLowest: $orderByLowest
            excludePrivateItemReviews: true
            first: $count
            after: $cursor
        ) @connection(key: "SellerReviewsMobileModalContent_paginatedFeedback") {
            metadata {
                aggregatedFeedback {
                    overallExperience {
                        mean
                    }
                }
            }
            numberOfPages
            edges {
                node {
                    serviceId
                    item {
                        serviceId
                        title
                        contemporaryTrackingString
                    }
                    publicNote {
                        ... on StandardQuestion {
                            language
                        }
                    }
                    ...Review_review
                }
            }
            ...SellerReviewsModalHeader_reviews
        }
        ...SellerReviewsModalHeader_viewer
    }
`;

const SellerReviewsMobileModalContentBase: FC<Props> = ({
    viewer,
    isLoading,
    sellerId,
    contentOnly,
}) => {
    const { data, hasNext, loadNext, refetch } = usePaginationFragment<
        SellerReviewsMobileModalContentRefetchQuery,
        SellerReviewsMobileModalContent_viewer$key
    >(viewerFragment, viewer);
    const intl = useIntl();
    const [isFetching, setIsFetching] = useState(false);
    const [isResetting, setIsResetting] = useState(false);
    const [translationState, setTranslationState] = useState<TranslationState>('HIDDEN');
    const reviews = data?.paginatedFeedback || null;
    const feedbackMean = reviews?.metadata?.aggregatedFeedback?.overallExperience?.mean;
    const feedbackFormPhotoUpload = !!data?.feedbackFormPhotoUpload;
    const avgOverallRating = feedbackMean && parseFloat(feedbackMean.toFixed(1));
    const normalizedReviews = normalizeReviews(reviews);
    const reviewBodyRef = useRef<HTMLDivElement>(null);
    const [activeTab, setActiveTab] = useState(REVIEWS_TAB);

    useEffect(() => {
        if (translationState === 'HIDDEN') {
            normalizedReviews?.forEach(review => {
                const { publicNote } = review || {};
                if (publicNote?.language && publicNote.language !== getLang(intl.locale)) {
                    setTranslationState('SHOW_ORIGINAL');
                }
            });
        }
    }, [intl.locale, normalizedReviews, translationState]);

    useEffect(() => {
        const currentReviews = normalizeReviews(reviews);
        const { ECOM_PRODUCT_IMPRESSION } = trackingConstants;
        const { EVENT_VIEW_ITEM_LIST } = eventNameConstants;

        if (currentReviews) {
            trackEcommerce({
                type: ECOM_PRODUCT_IMPRESSION,
                eventName: EVENT_VIEW_ITEM_LIST,
                actionField: {
                    list: 'sellerReviews',
                },
                products: currentReviews.map((review, index) => {
                    const item = review && review.item;
                    if (item) {
                        return {
                            id: item.serviceId,
                            name: item.title,
                            brand: sellerId,
                            dimension83: item.contemporaryTrackingString,
                            position: index,
                        };
                    }
                    return {};
                }),
            });
        }
    }, [reviews, sellerId]);

    const reviewRows =
        isResetting || isLoading ? (
            <div className={styles.spinnerWrapper}>
                <Spinner dark />
            </div>
        ) : (
            normalizedReviews
                .map(
                    review =>
                        review && (
                            <Review
                                review={review}
                                key={`seller-review-${
                                    review && review.serviceId ? review.serviceId : ''
                                }`}
                                showOrderPhoto={feedbackFormPhotoUpload}
                                translationState={translationState}
                            />
                        )
                )
                .filter(Boolean)
        );

    const getButtonState = useCallback(
        function () {
            if (isFetching) {
                return <Spinner size="tiny" containerClass={styles.spinner} />;
            } else if (hasNext) {
                return (
                    <FormattedMessage
                        defaultMessage="Load More"
                        id="sellerfeedback.modal.loadmore"
                    />
                );
            } else {
                return null;
            }
        },
        [isFetching, hasNext]
    );

    function handleGridScroll(oldScrollY: number): void {
        const reviewBodyEl = reviewBodyRef?.current;
        if (reviewBodyEl) {
            reviewBodyEl.scrollTo(reviewBodyEl.scrollLeft, oldScrollY);
        }
    }

    const onViewMoreClick = useCallback(
        function () {
            if (hasNext) {
                const oldScrollY = reviewBodyRef?.current?.scrollTop || 0;
                setIsFetching(true);
                loadNext(PAGE_SIZE, {
                    onComplete: () => {
                        setIsFetching(false);
                        handleGridScroll(oldScrollY);
                    },
                });
            }
        },
        [hasNext, loadNext, setIsFetching]
    );

    const onSortOrderChange = useCallback(
        async (order: OrderBy) => {
            const sortOrders = {
                [ORDER_BY_NEWEST]: order === ORDER_BY_NEWEST,
                [ORDER_BY_HIGHEST]: order === ORDER_BY_HIGHEST,
                [ORDER_BY_LOWEST]: order === ORDER_BY_LOWEST,
            };
            setIsResetting(true);
            refetch(
                {
                    ...sortOrders,
                    count: PAGE_SIZE,
                },
                { onComplete: () => setIsResetting(false) }
            );
        },
        [refetch]
    );

    return (
        <TabProvider activeTab={activeTab} setActiveTab={setActiveTab}>
            <div className={styles.reviewBodyWrapper}>
                <SellerReviewsModalHeader
                    isCompact={contentOnly}
                    avgOverallRating={avgOverallRating}
                    translationState={translationState}
                    onTranslateChange={setTranslationState}
                    onSortOrderChange={onSortOrderChange}
                    activeTab={activeTab}
                    viewer={data}
                    reviews={reviews}
                    isMobile
                />
                <div
                    className={classnames(styles.reviewsModalBody, {
                        [styles.compact]: contentOnly,
                    })}
                    ref={reviewBodyRef}
                >
                    <TabPanels>
                        {[
                            REVIEWS_TAB,
                            <Fragment key={REVIEWS_TAB}>
                                {reviewRows}
                                {!(isResetting || isLoading) && hasNext && (
                                    <Button
                                        className={styles.button}
                                        onClick={onViewMoreClick}
                                        size="small"
                                        type="secondary"
                                    >
                                        {getButtonState()}
                                    </Button>
                                )}
                            </Fragment>,
                        ]}
                        {feedbackFormPhotoUpload && [
                            PHOTOS_TAB,
                            <SellerReviewsPhotosCarouselRenderer
                                key={PHOTOS_TAB}
                                sellerId={sellerId}
                            />,
                        ]}
                    </TabPanels>
                </div>
            </div>
        </TabProvider>
    );
};

export const SellerReviewsMobileModalContent = SellerReviewsMobileModalContentBase;
